/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react"
import {
  Box,
  Button,
  TextField,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  Switch,
  FormControlLabel,
  Chip,
} from "@mui/material"
import { collection, addDoc, deleteDoc, doc, updateDoc, onSnapshot } from "firebase/firestore"
import { toast } from 'react-toastify'
import { db } from "../../firebase"
import { tokens } from "../../theme"
import { DataGrid } from "@mui/x-data-grid"
import dayjs from "dayjs"
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'

export interface IEmployee {
  id?: string
  name: string
  email: string
  active: boolean
  createdAt?: string
  updatedAt?: string
}

const emptyEmployee: IEmployee = {
  name: '',
  email: '',
  active: true,
}

const Employees = () => {
  const theme = useTheme()
  const colors = tokens(theme.palette.mode)
  const employeesRef = collection(db, 'employees')
  const [items, setItems] = useState<IEmployee[]>([])
  const [openDialog, setOpenDialog] = useState<boolean>(false)
  const [editMode, setEditMode] = useState<boolean>(false)
  const [current, setCurrent] = useState<IEmployee>(emptyEmployee)
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({})

  useEffect(() => {
    const unsubscribe = onSnapshot(employeesRef, ({ docs }) => {
      setItems(docs.map((d) => ({ id: d.id, ...d.data() } as IEmployee)))
    })
    return unsubscribe
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const columns = [
    {
      field: "name",
      headerName: "氏名",
      flex: 1,
    },
    {
      field: "email",
      headerName: "メールアドレス",
      flex: 1.5,
    },
    {
      field: "active",
      headerName: "状態",
      flex: 0.8,
      renderCell: (params: any) => (
        params.row.active
          ? <Chip label="有効" color="success" size="small" />
          : <Chip label="無効" size="small" />
      ),
    },
    {
      field: "updatedAt",
      headerName: "更新日時",
      flex: 1,
    },
    {
      field: "actions",
      headerName: "操作",
      flex: 0.8,
      renderCell: (params: any) => (
        <Box>
          <IconButton color="primary" onClick={() => handleEdit(params.row)} size="small">
            <EditIcon />
          </IconButton>
          <IconButton color="error" onClick={() => handleDelete(params.row.id)} size="small">
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    }
  ]

  const rows = [...items]
    .sort((a, b) => (a.name > b.name ? 1 : -1))
    .map((item, i) => ({ No: i + 1, ...item }))

  const handleEdit = (employee: IEmployee) => {
    setEditMode(true)
    setCurrent({
      id: employee.id,
      name: employee.name,
      email: employee.email,
      active: employee.active,
    })
    setFormErrors({})
    setOpenDialog(true)
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm("この従業員を削除しますか？\n※過去のチェック記録は削除されません")) return
    try {
      await deleteDoc(doc(db, "employees", id))
      toast.success("従業員を削除しました")
    } catch (e) {
      toast.error("削除に失敗しました: " + JSON.stringify(e))
    }
  }

  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {}

    if (!current.name.trim()) {
      errors.name = '氏名は必須です'
    }
    if (!current.email.trim()) {
      errors.email = 'メールアドレスは必須です'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(current.email.trim())) {
      errors.email = 'メールアドレスの形式が正しくありません'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSave = async () => {
    if (!validateForm()) return

    try {
      const timestamp = dayjs().format("YYYY-MM-DD HH:mm:ss")
      const employeeData = {
        name: current.name.trim(),
        email: current.email.trim(),
        active: current.active,
        updatedAt: timestamp
      }

      if (editMode && current.id) {
        await updateDoc(doc(db, "employees", current.id), employeeData)
        toast.success("従業員情報を更新しました")
      } else {
        await addDoc(employeesRef, {
          ...employeeData,
          createdAt: timestamp
        })
        toast.success("従業員を登録しました")
      }

      handleCloseDialog()
    } catch (e) {
      toast.error("保存に失敗しました: " + JSON.stringify(e))
    }
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
    setEditMode(false)
    setCurrent(emptyEmployee)
    setFormErrors({})
  }

  const handleOpenNewDialog = () => {
    setEditMode(false)
    setCurrent(emptyEmployee)
    setFormErrors({})
    setOpenDialog(true)
  }

  return (
    <Box m={5}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <h2>従業員マスタ</h2>
          <p>登録した従業員へ、毎朝9時に従業員健康チェック表の入力依頼メールが送信されます</p>
        </Box>
        <Button variant="contained" color="primary" onClick={handleOpenNewDialog}>
          新規従業員登録
        </Button>
      </Box>

      <Box
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": { border: "none" },
          "& .MuiDataGrid-cell": { borderBottom: "none" },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
        }}
      >
        <DataGrid rows={rows} columns={columns} getRowHeight={() => 'auto'} />
      </Box>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editMode ? '従業員編集' : '新規従業員登録'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="氏名 *"
                value={current.name}
                onChange={(e) => setCurrent(prev => ({ ...prev, name: e.target.value }))}
                error={!!formErrors.name}
                helperText={formErrors.name}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="メールアドレス *"
                type="email"
                value={current.email}
                onChange={(e) => setCurrent(prev => ({ ...prev, email: e.target.value }))}
                error={!!formErrors.email}
                helperText={formErrors.email || "Googleログインに使用するメールアドレスと一致させてください"}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={current.active}
                    onChange={(e) => setCurrent(prev => ({ ...prev, active: e.target.checked }))}
                    color="primary"
                  />
                }
                label={current.active ? "有効（通知対象）" : "無効（通知対象外）"}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="secondary">キャンセル</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            {editMode ? '更新' : '登録'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default Employees
